import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Phone, ChevronLeft, ChevronRight, X, ZoomIn } from 'lucide-react';
import CardGrid from './CardGrid';
import './CategoryLandingTemplate.css';

const imageBasePaths = {
  'movimiento-portuario': '/assets/tecnal/mp',
  'etanol-de-maiz': '/assets/tecnal/em',
  'aceite-vegetal': '/assets/tecnal/av',
  'alimentacion-animal': '/assets/tecnal/aa',
  'almacenamiento-de-granos': '/assets/tecnal/ag',
};

const GallerySection = ({ card, basePath }) => {
  const [selectedImage, setSelectedImage] = useState(0);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const images = card.images || [];

  useEffect(() => {
    if (!isLightboxOpen) return;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const handleKey = (e) => {
      if (e.key === 'Escape') setIsLightboxOpen(false);
      if (e.key === 'ArrowRight') setSelectedImage(prev => (prev + 1) % images.length);
      if (e.key === 'ArrowLeft') setSelectedImage(prev => (prev - 1 + images.length) % images.length);
    };
    window.addEventListener('keydown', handleKey);
    return () => {
      document.body.style.overflow = originalOverflow;
      window.removeEventListener('keydown', handleKey);
    };
  }, [isLightboxOpen, images.length]);

  if (images.length === 0) return null;

  const openLightbox = (index) => {
    setSelectedImage(index);
    setIsLightboxOpen(true);
  };

  return (
    <>
      <div className="category-gallery-grid">
        {images.map((img, index) => (
          <div
            key={index}
            className="category-gallery-item"
            onClick={() => openLightbox(index)}
          >
            <img
              src={`${basePath}/${img}`}
              alt={`${card.title} - Imagen ${index + 1}`}
              loading="lazy"
              className="category-gallery-image"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            <div className="category-gallery-overlay">
              <ZoomIn size={26} strokeWidth={1.5} />
            </div>
          </div>
        ))}
      </div>

      {isLightboxOpen && (
        <div className="lightbox-overlay" onClick={() => setIsLightboxOpen(false)}>
          <div className="lightbox-inner" onClick={(e) => e.stopPropagation()}>
            <button className="lightbox-close" onClick={() => setIsLightboxOpen(false)} aria-label="Cerrar">
              <X size={20} />
            </button>
            <div className="lightbox-image-area">
              {images.length > 1 && (
                <button
                  className="lightbox-nav prev"
                  onClick={() => setSelectedImage(prev => (prev - 1 + images.length) % images.length)}
                  aria-label="Imagen anterior"
                >
                  <ChevronLeft size={28} />
                </button>
              )}
              <img
                key={`lb-${selectedImage}`}
                src={`${basePath}/${images[selectedImage]}`}
                alt={`${card.title} - Imagen ${selectedImage + 1}`}
                className="lightbox-image"
              />
              {images.length > 1 && (
                <button
                  className="lightbox-nav next"
                  onClick={() => setSelectedImage(prev => (prev + 1) % images.length)}
                  aria-label="Siguiente imagen"
                >
                  <ChevronRight size={28} />
                </button>
              )}
            </div>
            <div className="lightbox-footer">
              <span className="lightbox-counter">{selectedImage + 1} / {images.length}</span>
              {images.length > 1 && (
                <div className="lightbox-thumbs">
                  {images.map((img, index) => (
                    <button
                      key={index}
                      className={`lightbox-thumb${index === selectedImage ? ' active' : ''}`}
                      onClick={() => setSelectedImage(index)}
                      aria-label={`Ver imagen ${index + 1}`}
                    >
                      <img
                        src={`${basePath}/${img}`}
                        alt=""
                        className="lightbox-thumb-img"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const CategoryLandingTemplate = ({ data }) => {
  const whatsappNumber = '5493413047536';
  const whatsappLink = `https://wa.me/${whatsappNumber}?text=¡Hola!`;

  // Determine category from slug
  const categorySlug = data.slug?.split('/').pop() || '';
  const basePath = imageBasePaths[categorySlug] || '/assets/tecnal';

  return (
    <div className="category-landing-container">
      {/* Hero Section */}
      <section className="category-hero">
        <div className="category-hero-content">
          <h1 className="category-hero-title">{data.title}</h1>
          {data.intro && data.intro.map((paragraph, idx) => (
            <p key={idx} className="category-hero-intro">{paragraph}</p>
          ))}
        </div>
      </section>

      {/* Dynamic Sections */}
      {data.sections && data.sections.map((section, idx) => {
        const galleryCards = section.cards.filter(c => c.type === 'gallery');
        const regularCards = section.cards.filter(c => c.type !== 'gallery');

        return (
          <section key={idx} className="category-section">
            <div className="container">
              {section.heading && (
                <h2 className="section-title">{section.heading}</h2>
              )}
              {regularCards.length > 0 && <CardGrid cards={regularCards} />}
              {galleryCards.map((card, j) => (
                <GallerySection key={j} card={card} basePath={basePath} />
              ))}
            </div>
          </section>
        );
      })}

      {/* CTA Section */}
      <section className="category-cta-section">
        <div className="container">
          <h2 className="category-cta-title">
            Representamos tecnología que potencia la eficiencia y confiabilidad de tus procesos.
          </h2>
          <Button 
            size="lg" 
            className="category-cta-button"
            onClick={() => window.open(whatsappLink, '_blank')}
          >
            <Phone className="mr-2" size={20} />
            Contactanos
          </Button>
        </div>
      </section>
    </div>
  );
};

export default CategoryLandingTemplate;
